/**
 * ⚠️ [Phase 56.5 — 2026-07-09] Panneau sticky-right "Sans job"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L1600-1797).
 *
 * Rôle : sur les 14 prochains jours, liste par jour les employés sans job
 * (non cédulés OU exclus d'un BT). Compteurs par jour, dépliable, avec
 * actions rapides (🔍 Diag, + Affecter, 📅 Congé, ✓ Réintégrer).
 *
 * Contrat :
 *   - `noJobWorkers` : { 'YYYY-MM-DD': [{employee_id, employee_nom, type, chef_nom, role, bt_id}] }
 *   - `expandedDays` : Set des dateStr dépliés
 *   - `onToggleExpand(dateStr)` : callback bascule expand
 *   - `onClose()` : ferme le panneau
 *   - `onDiag(employee_id, employee_nom, dateStr)` : ouvre diagnostic
 *   - `onAffecter(dateStr, employee_id, employee_nom)` : ouvre AffecterEmployeDialog
 *   - `onConge(dateStr, employee_id, employee_nom)` : ouvre CongeRapideDialog
 *   - `onReintegrer(o, dateStr)` : réintègre l'employé au BT
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { AlertTriangle, X, ChevronUp, ChevronDown } from 'lucide-react';

export default function SansJobPanel({
  noJobWorkers = {},
  expandedDays,
  onToggleExpand,
  onClose,
  onDiag,
  onAffecter,
  onConge,
  onReintegrer,
}) {
  const total = Object.values(noJobWorkers).reduce((s, v) => s + (v?.length || 0), 0);
  const orderedDates = Object.keys(noJobWorkers)
    .filter(d => (noJobWorkers[d] || []).length > 0)
    .sort();

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l border-amber-200 z-50 flex flex-col" data-testid="no-job-panel">
      {/* Header STICKY */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-b border-amber-200 px-4 py-3 flex items-start justify-between sticky top-0 z-10 shrink-0">
        <div className="min-w-0 pr-2">
          <h2 className="font-semibold text-amber-900 flex items-center gap-2 text-base">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>Sans job</span>
            {total > 0 && (
              <span className="ml-1 bg-amber-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">{total}</span>
            )}
          </h2>
          <p className="text-[11px] text-amber-700/80 mt-1 leading-relaxed">
            14 prochains jours · cliquez sur un jour pour voir les employés.
          </p>
        </div>
        <button
          onClick={() => onClose?.()}
          className="shrink-0 rounded-full w-8 h-8 flex items-center justify-center text-amber-700 hover:text-white hover:bg-amber-600 transition border border-amber-300 hover:border-amber-600 bg-white/60"
          aria-label="Fermer le panneau"
          data-testid="btn-close-no-job-panel"
          title="Fermer"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>

      {/* Corps scrollable */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {orderedDates.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <span className="text-4xl">✅</span>
            <p className="text-sm mt-3 font-medium text-slate-600">Tous les employés ont une job</p>
            <p className="text-xs mt-1">pour les 14 prochains jours</p>
          </div>
        ) : (
          orderedDates.map(dateStr => {
            const orphelins = noJobWorkers[dateStr] || [];
            const d = new Date(dateStr + 'T00:00:00');
            const isExpanded = expandedDays && expandedDays.has ? expandedDays.has(dateStr) : false;
            const labelJour = d.toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' });
            const isToday = dateStr === new Date().toISOString().substring(0, 10);
            return (
              <div
                key={dateStr}
                className={`border rounded-lg overflow-hidden bg-white transition ${
                  isExpanded ? 'border-amber-400 shadow-sm' : 'border-slate-200 hover:border-amber-300'
                }`}
                data-testid={`no-job-day-card-${dateStr}`}
              >
                <button
                  type="button"
                  onClick={() => onToggleExpand?.(dateStr)}
                  className={`w-full px-3 py-2.5 flex items-center justify-between gap-2 transition ${
                    isExpanded ? 'bg-amber-50' : 'bg-white hover:bg-amber-50/60'
                  }`}
                  data-testid={`no-job-day-toggle-${dateStr}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center text-[10px] uppercase font-semibold ${
                      isToday ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800'
                    }`}>
                      <span className="leading-none">{d.toLocaleDateString('fr-CA', { month: 'short' }).replace('.', '')}</span>
                      <span className="text-base font-bold leading-none mt-0.5">{d.getDate()}</span>
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-semibold text-slate-800 capitalize truncate">
                        {labelJour}{isToday && <span className="ml-1.5 text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">aujourd'hui</span>}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {orphelins.length} employé{orphelins.length > 1 ? 's' : ''} sans job
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      orphelins.length >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {orphelins.length}
                    </span>
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 text-amber-600" />
                      : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="divide-y divide-slate-100 bg-white">
                    {orphelins.map((o, i) => {
                      const isNonCedule = o.type === 'non_cedule';
                      return (
                        <div
                          key={o.employee_id ? `${o.employee_id}-${o.type || 'x'}-${i}` : `${JSON.stringify(o)}-${i}`}
                          className="px-3 py-2.5 flex items-start gap-2"
                        >
                          <span className={`mt-0.5 text-base shrink-0 ${isNonCedule ? 'text-orange-500' : 'text-amber-500'}`}>
                            {isNonCedule ? '🟠' : '⚠'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{o.employee_nom}</p>
                            {isNonCedule ? (
                              <p className="text-[11px] text-orange-600 mt-0.5">
                                Non cédulé{o.role ? ` · ${o.role}` : ''}
                              </p>
                            ) : (
                              <p className="text-[11px] text-slate-500 mt-0.5">Exclu d'un BT · Équipe {o.chef_nom}</p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              <button
                                onClick={() => onDiag?.(o.employee_id, o.employee_nom, dateStr)}
                                className="text-[10px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded px-2 py-1 transition"
                                data-testid={`btn-diag-${o.employee_id}-${dateStr}`}
                                title="Diagnostiquer pourquoi cet employé est marqué sans job"
                              >
                                🔍 Diag
                              </button>
                              {isNonCedule && (
                                <>
                                  <button
                                    onClick={() => onAffecter?.(dateStr, o.employee_id, o.employee_nom)}
                                    className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded px-2 py-1 transition"
                                    data-testid={`btn-affecter-${o.employee_id}-${dateStr}`}
                                    title="Affecter à un BT du jour"
                                  >
                                    + Affecter
                                  </button>
                                  <button
                                    onClick={() => onConge?.(dateStr, o.employee_id, o.employee_nom)}
                                    className="text-[10px] font-semibold text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 rounded px-2 py-1 transition"
                                    data-testid={`btn-conge-${o.employee_id}-${dateStr}`}
                                    title="Marquer en congé pour ce jour"
                                  >
                                    📅 Congé
                                  </button>
                                </>
                              )}
                              {!isNonCedule && o.bt_id && (
                                <button
                                  onClick={() => onReintegrer?.(o, dateStr)}
                                  className="text-[10px] font-semibold text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded px-2 py-1 transition"
                                  data-testid={`btn-reintegrer-${o.employee_id}-${dateStr}`}
                                  title="Le retirer de « Retirés » et l'assigner au BT"
                                >
                                  ✓ Réintégrer
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer compact avec légende */}
      <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-[10px] text-slate-500 shrink-0 flex items-center justify-between">
        <span>⚠ Exclu d'un BT</span>
        <span>🟠 Non cédulé</span>
      </div>
    </div>
  );
}
